import React from 'react';
import { Alert, Button, PrizeWheel, useResponsive, useToast } from '@parsina/framework';
import { bffMutation, useClinicalTranslation } from '@parsina/framework/clinical';
import { C, RADIUS, soft } from '@parsina/framework/tokens';

/**
 * «گردونهٔ شانس» — the wheel, when a campaign owes this doctor a spin.
 *
 * A port of the doctor panel's `PrizeStage` (doctor-app src/screens/Onboarding).
 * Same rules, same words — the clinical catalogue's `auth.onboarding.prize.*`,
 * which is why this reads `useClinicalTranslation` and is mounted inside
 * `<ClinicalI18n>`. Change it in the doctor app first.
 *
 * PROMOTION NOTE (packages/DESIGN.md rule 4): two apps now draw this stage, so
 * it belongs in `packages/clinical` beside the wheel and the words it already
 * ships. It is a copy today only because promoting it is a framework release
 * and a pin bump in both apps; do that before a third app needs it.
 *
 * THE SERVER DECIDES. This screen does not pick a slice, does not know the
 * odds, and cannot change the answer by asking twice: it sends a draw id and
 * animates to whatever index comes back. A failure is never a dead end.
 *
 * ── What is NOT here ───────────────────────────────────────────────────────
 *
 * The doctor panel calls `notifyWalletChanged()` after a win so its credits
 * hero refetches. This panel has no such display — a practitioner's پارسه is
 * personal and lives in the doctor panel (lib/doctorPanel.js), and the clinic's
 * own balance is a different wallet that this prize never touches — so there is
 * nothing here to invalidate.
 */

// `canRetry: true` is this client saying it knows what «تلاش دوباره» is. The
// server may only land a draw on that slice for a client that says so — one
// that does not would show a doctor «۰ پارسه» on a result card and let them
// walk away from a prize they are still owed.
const SPIN_PRIZE_WHEEL = `
  mutation SpinPrizeWheel($drawId: ID!, $canRetry: Boolean) {
    spinPrizeWheel(drawId: $drawId, canRetry: $canRetry) {
      id status segmentIndex kind amount smartVisits
      whatsappMessages baleMessages smsMessages retry
      balance { balance currency }
    }
  }
`;

// The wheel's diameter. Both shrink to their container — the kit's svg is
// `max-width: 100%` — so these are a ceiling rather than a size.
const WHEEL_PX = { mobile: 318, desktop: 380 };

/** The winning slice, or null. 0 IS a slice, so `||` and `??` are both wrong. */
const settledIndex = (draw) =>
  (Number.isInteger(draw?.segmentIndex) ? draw.segmentIndex : null);

/** A draw the doctor has already spun — re-shown, not replayed. */
const alreadySpun = (draw) =>
  draw?.status !== 'offered' && settledIndex(draw) != null;

/** The wheel stopped on «تلاش دوباره»: nothing was won and the draw is STILL
 *  `offered`. The server lands a draw here at most once, so the spin after it
 *  always wins. Read off the server's own flag — never off a label or an
 *  amount of 0, which a real (if stingy) slice could also carry. */
const landedOnRetry = (draw) => draw?.retry === true;

function CheckIcon({ size = 26 }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

export default function DoctorPrizeStep({ draw, onDone }) {
  const { t, has, formatNumber } = useClinicalTranslation();
  const { isMobile } = useResponsive();
  const toast = useToast();

  // A draw that arrives already spun opens AT its result: the wheel sits on the
  // winning slice with no animation and the card is there on the first frame.
  // Replaying a four-second spin for a prize decided yesterday would read as a
  // second chance. A draw that landed on «تلاش دوباره» (the doctor closed the
  // tab between the two spins) is kept as well, but NOT as settled.
  const [result, setResult] = React.useState(
    () => (alreadySpun(draw) || landedOnRetry(draw) ? draw : null));
  const [spinning, setSpinning] = React.useState(false);
  // `settled` means A PRIZE is on screen — it is what swaps the button for the
  // result card and what makes `spin` a no-op. A retry never sets it.
  const [settled, setSettled] = React.useState(() => alreadySpun(draw));
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  const segments = Array.isArray(draw?.segments) ? draw.segments : [];
  const targetIndex = settledIndex(result);

  const spin = async () => {
    if (busy || spinning || settled) return;
    setBusy(true);
    setError('');
    try {
      // The same draw id both times: the second chance is the server's to give,
      // and it is the same draw being spun again.
      const data = await bffMutation(SPIN_PRIZE_WHEEL, { drawId: draw.id, canRetry: true });
      const spun = data?.spinPrizeWheel;
      // A 200 with no draw in it. Not an exception — the request succeeded — so
      // it is reported as the same sentence as a failure rather than thrown into
      // the catch, where the code/messageKey branches would find nothing.
      if (!spun) { setError(t('errors.save.generic')); return; }

      setResult(spun);
      const index = settledIndex(spun);
      if (index == null) {
        // The service answered without a slice. There is nothing to animate to,
        // and inventing one (index 0 is a real prize) would show a number that
        // was not won. Skip the wheel, keep the result — unless it is a retry,
        // which has no result to keep.
        if (!landedOnRetry(spun)) setSettled(true);
      } else {
        setSpinning(true);
      }
    } catch (e) {
      if (e?.extensions?.code === 'PRIZE_NOT_AVAILABLE') {
        // The wheel stopped being spinnable between this page rendering and the
        // button. Say so and move on — there is no second action on this screen,
        // so a doctor left here with a dead button is a doctor who cannot reach
        // their clinic.
        toast?.info?.(t('auth.onboarding.prize.unavailable'));
        onDone?.();
        return;
      }
      // The BFF names the sentence it means; its own text is the fallback for a
      // key this catalogue has not learned yet.
      const key = e?.extensions?.messageKey;
      setError((key && has(key) ? t(key) : '') || e?.message || t('errors.save.generic'));
    } finally {
      setBusy(false);
    }
  };

  // The wheel has come to rest. A win settles the stage; a retry only stops the
  // wheel — it stays parked on that slice, which is what lets the next spin
  // carry on from there — and the button comes back.
  const onWheelSettled = () => {
    setSpinning(false);
    if (!landedOnRetry(result)) setSettled(true);
  };

  const amount = Number(result?.amount || 0);
  // The service's own figures. Absent (an older BFF) → the line is dropped
  // rather than guessed at: a wrong equivalence is worse than none.
  const smartVisits = Number(result?.smartVisits || 0);
  const whatsappMessages = Number(result?.whatsappMessages || 0);
  const baleMessages = Number(result?.baleMessages || 0);
  const smsMessages = Number(result?.smsMessages || 0);
  const newBalance = result?.balance?.balance;
  const showResult = settled && result;
  // «تلاش دوباره» is on screen from the moment the wheel stops on it until the
  // second spin is pressed — not while it is still turning, which would give
  // the animation away exactly as the result card would.
  const retryPending = !settled && landedOnRetry(result);
  const showRetry = retryPending && !busy && !spinning;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }} data-testid="onboarding-prize-step">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: '1.1875rem', fontWeight: 800, color: C.textPrimary, lineHeight: 1.6 }}>
          {draw?.title || t('auth.onboarding.prize.title')}
        </div>
        {draw?.subtitle ? (
          <div style={{ fontSize: '0.8125rem', fontWeight: 500, color: C.textSecondary, lineHeight: 1.9 }}>
            {draw.subtitle}
          </div>
        ) : null}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <PrizeWheel
          data-testid="prize-wheel"
          segments={segments}
          targetIndex={targetIndex}
          spinning={spinning}
          onSettled={onWheelSettled}
          size={isMobile ? WHEEL_PX.mobile : WHEEL_PX.desktop}
          label={t('auth.onboarding.prize.title')}
        />
      </div>

      {error ? <Alert variant="error" data-testid="prize-error">{error}</Alert> : null}

      {showResult ? (
        <div
          data-testid="prize-result"
          style={{
            display: 'flex', flexDirection: 'column', gap: 12,
            padding: 16, borderRadius: RADIUS.card,
            background: C.card, border: `1px solid ${C.border}`,
            boxShadow: `0 6px 24px ${soft(C.navy, 0.1)}`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span
              aria-hidden="true"
              style={{
                width: 44, height: 44, borderRadius: RADIUS.control, flexShrink: 0,
                background: C.teal, color: C.card,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            ><CheckIcon /></span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: '0.9375rem', fontWeight: 700, color: C.textPrimary, lineHeight: 1.6 }}>
                {/* The wallet has not confirmed → the prize is on its way, and NO
                    balance is printed. Saying «اضافه شد» over a number that has
                    not moved is the one thing this card must never do. */}
                {newBalance != null
                  ? t('auth.onboarding.prize.wonParseh', { parseh: formatNumber(amount) })
                  : t('auth.onboarding.prize.wonPending', { parseh: formatNumber(amount) })}
              </div>
              {/* What the prize is WORTH. Every figure is DERIVED BY THE SERVICE
                  from the real prices, never worked out here: a second copy of a
                  price in a client is free to drift the day that price changes. */}
              {whatsappMessages > 0 ? (
                <div
                  data-testid="prize-worth"
                  style={{ fontSize: '0.71875rem', color: C.textSecondary, lineHeight: 1.9, overflowWrap: 'anywhere' }}
                >
                  {t('auth.onboarding.prize.worth', {
                    whatsapp: formatNumber(whatsappMessages),
                    bale: formatNumber(baleMessages),
                    sms: formatNumber(smsMessages),
                    visits: formatNumber(smartVisits),
                  })}
                </div>
              ) : smartVisits > 0 ? (
                // An older BFF, which knows smart visits and nothing else.
                <div style={{ fontSize: '0.71875rem', color: C.textSecondary }}>
                  {t('auth.onboarding.prize.smartVisits', { visits: formatNumber(smartVisits) })}
                </div>
              ) : null}
            </div>
          </div>

          {newBalance != null ? (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 12px', borderRadius: RADIUS.control, background: C.bg,
            }}>
              <span style={{ fontSize: '0.71875rem', color: C.textSecondary }}>
                {t('auth.onboarding.prize.newBalance')}
              </span>
              <span style={{ fontSize: '0.9375rem', fontWeight: 700, color: C.navy }} dir="ltr">
                {formatNumber(newBalance)}
              </span>
            </div>
          ) : null}

          <Button
            size="lg"
            data-testid="prize-continue"
            onClick={() => onDone?.()}
            className="min-h-11 font-bold w-full bg-gradient-brand"
          >{t('auth.onboarding.prize.continue')}</Button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* NO «ادامه» here, on purpose: nothing has been won yet and the next
              spin is guaranteed to win, so a way out would be a way to abandon a
              prize. The screen is still never a trap — PRIZE_NOT_AVAILABLE on
              the second spin moves on exactly as it does on the first. */}
          {showRetry ? (
            <div
              role="status"
              data-testid="prize-retry"
              style={{
                display: 'flex', flexDirection: 'column', gap: 4,
                padding: '0.75rem 0.875rem', borderRadius: RADIUS.card,
                background: soft(C.teal, 0.08), border: `1px solid ${soft(C.teal, 0.3)}`,
                textAlign: 'start',
              }}
            >
              <div style={{ fontSize: '0.9375rem', fontWeight: 700, color: C.textPrimary, lineHeight: 1.6 }}>
                {t('auth.onboarding.prize.retryTitle')}
              </div>
              <div style={{ fontSize: '0.8125rem', fontWeight: 500, color: C.textSecondary, lineHeight: 1.9 }}>
                {t('auth.onboarding.prize.retryBody')}
              </div>
            </div>
          ) : null}
          <Button
            size="lg"
            data-testid="prize-spin"
            onClick={spin}
            disabled={busy || spinning}
            className="min-h-11 font-bold w-full bg-gradient-brand"
          >
            {busy || spinning
              ? t('auth.onboarding.prize.spinning')
              : t(retryPending ? 'auth.onboarding.prize.spinAgain' : 'auth.onboarding.prize.spin')}
          </Button>
          {/* «فقط یک بار» under a notice that says "spin again" would contradict
              it, so the caption steps aside for as long as a retry is owed. */}
          {!retryPending ? (
            <div style={{ fontSize: '0.71875rem', color: C.textMuted, textAlign: 'center', lineHeight: 1.8 }}>
              {t('auth.onboarding.prize.oneSpin')}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
